import React, { useState, useEffect } from "react";
import {
  Tag,
  Save,
  RotateCcw,
  Eye,
  EyeOff,
  Plus,
  Trash2,
  ExternalLink,
  AlertCircle,
  Play,
  RefreshCw,
} from "lucide-react";
import {
  getOffers,
  saveOffers,
  resetOffers,
  getPopupConfig,
  savePopupConfig,
  resetPopupSeen,
  resetAllPopupSeen,
} from "@/lib/offersStore";

const DAY_LABELS = [
  { iso: 1, label: "Lun" },
  { iso: 2, label: "Mar" },
  { iso: 3, label: "Mer" },
  { iso: 4, label: "Jeu" },
  { iso: 5, label: "Ven" },
  { iso: 6, label: "Sam" },
  { iso: 7, label: "Dim" },
];

const newEmptyOffer = () => ({
  id: `offre-${Date.now()}`,
  title: "Nouvelle offre",
  description: "",
  price: "0,00 €",
  conditions: "",
  composition: [],
  drink_hint: null,
  drink_rule: null,
  media_type: null,
  media_url: null,
  pdf_url: null,
  active: false,
  show_on_offers_page: true,
  popup_enabled: false,
  popup_frequency: "once_per_day",
  start_date: null,
  end_date: null,
  active_days: [],
  start_time: null,
  end_time: null,
  cta_label: "Voir l'offre",
  cta_target: "",
});

const FieldLabel = ({ children }) => (
  <label className="block text-[10px] uppercase tracking-[0.22em] text-white/50 font-street mb-1.5">
    {children}
  </label>
);

const inputCls =
  "w-full rounded-md border border-[#262626] bg-[#0A0A0A] px-3 py-2 text-sm text-white placeholder-white/30 focus:border-[#FF9500] focus:outline-none";

const OfferEditor = ({ offer, onChange, onDelete }) => {
  const set = (key, value) => onChange({ ...offer, [key]: value });

  const toggleDay = (iso) => {
    const cur = new Set(offer.active_days || []);
    if (cur.has(iso)) cur.delete(iso);
    else cur.add(iso);
    set("active_days", Array.from(cur).sort((a, b) => a - b));
  };

  return (
    <article className="rounded-xl border border-[#262626] bg-[#121212] p-4 sm:p-5">
      <header className="flex items-start gap-3 mb-4">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-[#FF9500] text-black shrink-0">
          <Tag size={16} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-street text-lg tracking-wide truncate">
            {offer.title || "Sans titre"}
          </div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-street">
            id : <span className="text-white/70">{offer.id}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-street ${
              offer.active
                ? "border border-[#FF9500]/40 bg-[#FF9500]/10 text-[#FF9500]"
                : "border border-white/15 bg-white/5 text-white/50"
            }`}
          >
            {offer.active ? <Eye size={11} /> : <EyeOff size={11} />}
            {offer.active ? "actif" : "inactif"}
          </span>
          <button
            type="button"
            aria-label="Supprimer l'offre"
            data-testid={`admin-offer-delete-${offer.id}`}
            onClick={() => onDelete(offer.id)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-[#DC2626]/40 text-[#DC2626] hover:bg-[#DC2626]/10"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </header>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <FieldLabel>Titre</FieldLabel>
          <input
            className={inputCls}
            value={offer.title || ""}
            data-testid={`admin-offer-title-${offer.id}`}
            onChange={(e) => set("title", e.target.value)}
          />
        </div>
        <div>
          <FieldLabel>Prix</FieldLabel>
          <input
            className={inputCls}
            value={offer.price || ""}
            placeholder="6,50 €"
            onChange={(e) => set("price", e.target.value)}
          />
        </div>
        <div className="sm:col-span-2">
          <FieldLabel>Description courte</FieldLabel>
          <textarea
            rows={2}
            className={inputCls}
            value={offer.description || ""}
            onChange={(e) => set("description", e.target.value)}
          />
        </div>
        <div className="sm:col-span-2">
          <FieldLabel>Conditions (texte discret)</FieldLabel>
          <input
            className={inputCls}
            value={offer.conditions || ""}
            placeholder="Voir conditions en boutique"
            onChange={(e) => set("conditions", e.target.value)}
          />
        </div>
        <div>
          <FieldLabel>URL image (jpg / png / webp)</FieldLabel>
          <input
            className={inputCls}
            value={offer.media_url || ""}
            placeholder="https://…"
            onChange={(e) =>
              onChange({
                ...offer,
                media_url: e.target.value || null,
                media_type: e.target.value ? "image" : null,
              })
            }
          />
        </div>
        <div>
          <FieldLabel>URL PDF (flyer)</FieldLabel>
          <input
            className={inputCls}
            value={offer.pdf_url || ""}
            placeholder="https://…/flyer.pdf"
            onChange={(e) => set("pdf_url", e.target.value || null)}
          />
        </div>

        <div>
          <FieldLabel>Date de début</FieldLabel>
          <input
            type="date"
            className={inputCls}
            value={offer.start_date || ""}
            onChange={(e) => set("start_date", e.target.value || null)}
          />
        </div>
        <div>
          <FieldLabel>Date de fin</FieldLabel>
          <input
            type="date"
            className={inputCls}
            value={offer.end_date || ""}
            onChange={(e) => set("end_date", e.target.value || null)}
          />
        </div>

        <div className="sm:col-span-2">
          <FieldLabel>Jours actifs (vide = tous)</FieldLabel>
          <div className="flex flex-wrap gap-1.5">
            {DAY_LABELS.map((d) => {
              const on = (offer.active_days || []).includes(d.iso);
              return (
                <button
                  key={d.iso}
                  type="button"
                  onClick={() => toggleDay(d.iso)}
                  className={`rounded-md px-3 py-1.5 text-xs font-street tracking-wider transition-colors ${
                    on
                      ? "bg-[#FF9500] text-black"
                      : "border border-[#262626] bg-[#0A0A0A] text-white/60 hover:text-white"
                  }`}
                >
                  {d.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <FieldLabel>Heure de début</FieldLabel>
          <input
            type="time"
            className={inputCls}
            value={offer.start_time || ""}
            onChange={(e) => set("start_time", e.target.value || null)}
          />
        </div>
        <div>
          <FieldLabel>Heure de fin</FieldLabel>
          <input
            type="time"
            className={inputCls}
            value={offer.end_time || ""}
            onChange={(e) => set("end_time", e.target.value || null)}
          />
        </div>

        <div>
          <FieldLabel>Libellé CTA</FieldLabel>
          <input
            className={inputCls}
            value={offer.cta_label || ""}
            onChange={(e) => set("cta_label", e.target.value)}
          />
        </div>
        <div>
          <FieldLabel>Cible CTA</FieldLabel>
          <input
            className={inputCls}
            value={offer.cta_target || ""}
            placeholder={`/offres#${offer.id}`}
            onChange={(e) => set("cta_target", e.target.value)}
          />
        </div>

        <div className="sm:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
          <Toggle
            label="Actif"
            value={offer.active}
            onChange={(v) => set("active", v)}
            testId={`admin-offer-active-${offer.id}`}
          />
          <Toggle
            label="Page Offres"
            value={offer.show_on_offers_page}
            onChange={(v) => set("show_on_offers_page", v)}
          />
          <Toggle
            label="Popup activé"
            value={offer.popup_enabled}
            onChange={(v) => set("popup_enabled", v)}
            testId={`admin-offer-popup-${offer.id}`}
          />
          <div>
            <FieldLabel>Fréquence popup</FieldLabel>
            <select
              className={inputCls}
              value={offer.popup_frequency || "once_per_day"}
              onChange={(e) => set("popup_frequency", e.target.value)}
            >
              <option value="once_per_session">1× par session</option>
              <option value="once_per_day">1× par jour</option>
            </select>
          </div>
        </div>
      </div>
    </article>
  );
};

const Toggle = ({ label, value, onChange, testId }) => (
  <label className="flex items-center gap-2 rounded-md border border-[#262626] bg-[#0A0A0A] px-3 py-2.5 cursor-pointer">
    <input
      type="checkbox"
      data-testid={testId}
      checked={!!value}
      onChange={(e) => onChange(e.target.checked)}
      className="h-4 w-4 accent-[#FF9500]"
    />
    <span className="text-[11px] uppercase tracking-[0.18em] font-street text-white/80">
      {label}
    </span>
  </label>
);

const AdminOffers = () => {
  const [offers, setOffers] = useState([]);
  const [popupCfg, setPopupCfg] = useState(getPopupConfig());
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setOffers(getOffers());
  }, []);

  const handleSave = () => {
    saveOffers(offers);
    savePopupConfig(popupCfg);
    setSaved(true);
    setTimeout(() => setSaved(false), 2200);
  };

  const handleReset = () => {
    resetOffers();
    setOffers(getOffers());
    offers.forEach((o) => resetPopupSeen(o.id));
  };

  const handleAdd = () => {
    setOffers((prev) => [...prev, newEmptyOffer()]);
  };

  const handleChange = (idx, next) => {
    setOffers((prev) => prev.map((o, i) => (i === idx ? next : o)));
  };

  const handleDelete = (id) => {
    setOffers((prev) => prev.filter((o) => o.id !== id));
  };

  return (
    <div data-testid="admin-offers-root" className="text-white">
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF9500] font-street">
            Administration
          </div>
          <h1 className="font-street mt-2 text-3xl sm:text-4xl tracking-wide leading-none">
            Offres & Popup
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <a
            href="/offres"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md border border-[#262626] bg-[#121212] px-3 py-2 text-xs font-street tracking-wider text-white/70 hover:text-white"
          >
            Voir page <ExternalLink size={12} />
          </a>
          <button
            type="button"
            onClick={handleReset}
            data-testid="admin-offers-reset"
            className="inline-flex items-center gap-1.5 rounded-md border border-[#262626] bg-[#121212] px-3 py-2 text-xs font-street tracking-wider text-white/70 hover:text-white"
          >
            <RotateCcw size={12} />
            Réinitialiser
          </button>
          <button
            type="button"
            onClick={handleAdd}
            data-testid="admin-offers-add"
            className="inline-flex items-center gap-1.5 rounded-md border border-[#FF9500]/40 bg-[#FF9500]/10 px-3 py-2 text-xs font-street tracking-wider text-[#FF9500] hover:bg-[#FF9500]/20"
          >
            <Plus size={12} />
            Nouvelle offre
          </button>
          <button
            type="button"
            onClick={handleSave}
            data-testid="admin-offers-save"
            className="inline-flex items-center gap-1.5 rounded-md bg-[#FF9500] px-4 py-2 text-xs font-street tracking-wider text-black shadow-[0_3px_0_#7a4900] hover:-translate-y-0.5 transition-all"
          >
            <Save size={12} />
            Enregistrer
          </button>
        </div>
      </header>

      {saved && (
        <div
          data-testid="admin-offers-saved-toast"
          className="mb-4 rounded-md border border-[#FF9500]/40 bg-[#FF9500]/10 px-3 py-2 text-xs text-[#FF9500] font-street tracking-wider"
        >
          Modifications enregistrées localement (localStorage).
        </div>
      )}

      {/* Note limites */}
      <div className="mb-6 rounded-md border border-yellow-500/30 bg-yellow-500/5 p-3 text-[12px] text-yellow-200/80 leading-relaxed flex gap-2">
        <AlertCircle size={14} className="shrink-0 mt-0.5" />
        <div>
          Phase 0 — Persistance via <span className="font-mono text-[11px]">localStorage</span>.
          L'upload natif d'images / PDF n'est pas encore en place : pour ajouter un visuel
          ou un flyer, collez une URL publique (hébergeur, drive, etc.). La migration
          vers MongoDB et l'upload réel seront branchés en Phase 1.
        </div>
      </div>

      {/* Popup global config */}
      <section className="mb-8 rounded-2xl border border-[#262626] bg-[#0F0F0F] p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div className="font-street text-sm tracking-wider uppercase text-[#FF9500]">
            Popup site
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => {
                resetAllPopupSeen();
                window.alert("Suivi popup réinitialisé. Le popup pourra réapparaître.");
              }}
              data-testid="admin-popup-reset-seen"
              className="inline-flex items-center gap-1.5 rounded-md border border-[#262626] bg-[#0A0A0A] px-3 py-1.5 text-[11px] font-street tracking-wider text-white/75 hover:text-white"
              title="Vide localStorage.le11.popup.seen.v1 — utile pour retester le popup le même jour."
            >
              <RefreshCw size={12} />
              Réinitialiser le suivi
            </button>
            <a
              href={`/?preview-popup=${popupCfg.offer_id || "1"}`}
              target="_blank"
              rel="noreferrer"
              data-testid="admin-popup-preview"
              className="inline-flex items-center gap-1.5 rounded-md bg-[#FF9500] px-3 py-1.5 text-[11px] font-street tracking-wider text-black shadow-[0_3px_0_#7a4900] hover:-translate-y-0.5 transition-all"
              title="Ouvre le site public en forçant l'affichage du popup, sans tenir compte des horaires ni du 'déjà vu'."
            >
              <Play size={12} />
              Tester le popup
            </a>
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-3 items-end">
          <Toggle
            label="Popup activé globalement"
            value={popupCfg.enabled}
            onChange={(v) => setPopupCfg({ ...popupCfg, enabled: v })}
            testId="admin-popup-enabled"
          />
          <div>
            <FieldLabel>Offre à afficher</FieldLabel>
            <select
              className={inputCls}
              value={popupCfg.offer_id || ""}
              onChange={(e) =>
                setPopupCfg({ ...popupCfg, offer_id: e.target.value })
              }
            >
              <option value="">— Aucune —</option>
              {offers
                .filter((o) => o.popup_enabled)
                .map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.title}
                  </option>
                ))}
            </select>
          </div>
          <div>
            <FieldLabel>Délai avant apparition (ms)</FieldLabel>
            <input
              type="number"
              min={0}
              step={100}
              className={inputCls}
              value={popupCfg.delay_ms ?? 1500}
              onChange={(e) =>
                setPopupCfg({ ...popupCfg, delay_ms: Number(e.target.value) || 0 })
              }
            />
          </div>
        </div>
      </section>

      {/* Liste des offres */}
      <div className="space-y-4">
        {offers.length === 0 && (
          <div className="rounded-2xl border border-[#262626] bg-[#121212] p-6 text-center text-sm text-[#A1A1AA]">
            Aucune offre. Cliquez sur « Nouvelle offre » pour en créer une.
          </div>
        )}
        {offers.map((o, i) => (
          <OfferEditor
            key={o.id}
            offer={o}
            onChange={(next) => handleChange(i, next)}
            onDelete={handleDelete}
          />
        ))}
      </div>
    </div>
  );
};

export default AdminOffers;
